import { useEffect, useMemo } from "react";
import { useFilters, usePagination, useSortBy, useTable } from "react-table";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { dispatch } from "../../../state/store";
import { setDeviceToEdit } from "../../../state/ducks";

import { t } from "../../../services";
import { strCode } from "../../../consts";
import { useTableData } from "../../../hooks";

import {
  DoubleButton,
  FlexContainers,
  IconTextButton,
  Paragraph2,
  SmallButton,
  SVG,
  Table,
  TablePagination,
  WrapperContainer
} from "../../../components";
import { DeviceTableColumns } from "./DeviceTableColumns";
import { CertificatesButtonText, DeviceTableWrapper } from "./styled";
import FilterTabContainer from "../Filters/FilterTabContainer";
import DeviceFiltersTab from "../Filters/DeviceFiltersTab";

const DeviceTable = ({
  devicesData = [],
  dusnFilter,
  setSearchValue,
  onCreateDevice,
  onCreateBatch,
  onOpenCertificates
}) => {
  const loading = useSelector((store) => store.fleetManagement?.loading);
  const error = useSelector((store) => store.fleetManagement?.error);

  const deviceGroupList = useSelector((store) => store.deviceGroups.deviceGroupsList);

  const { deviceType, devices, groups } = useSelector(
    (store) => store.fleetManagement?.deviceFilters
  );

  const deviceRows = useMemo(() => {
    if (!devicesData) return [];

    return devicesData.map((x) => {
      return {
        ...x,
        groups: deviceGroupList?.find((groupList) => groupList.dusn === x.dusn)?.groupIds ?? []
      };
    });
  }, [devicesData, deviceGroupList]);

  const data = useTableData({
    loading: loading && deviceRows?.length === 0,
    data: deviceRows
  });

  const columns = useMemo(() => DeviceTableColumns, []);
  const tableInstance = useTable(
    {
      columns,
      data
    },
    useFilters,
    useSortBy,
    usePagination
  );
  const {
    rows, // total rows
    setFilter,
    nextPage, // pagination props
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageCount,
    setPageSize,
    state: { pageIndex, pageSize }
  } = tableInstance;

  useEffect(() => {
    setFilter("dusn", dusnFilter ? dusnFilter : null);
    setFilter("deviceName", { model: devices, type: deviceType?.toLowerCase(), groups: groups });
  }, [dusnFilter, devices, deviceType, groups]);

  const onEdit = (device) => {
    dispatch(setDeviceToEdit(device));
  };

  return (
    <WrapperContainer width="100%">
      <FilterTabContainer
        searchValue={dusnFilter}
        setSearchValue={setSearchValue}
        searchPlaceholder={t(strCode.SEARCH_FOR_SERIAL_NUMBER)}
        ControlsComponent={
          <WrapperContainer Container={FlexContainers.FlexRow} gap={12}>
            <SmallButton onClick={onOpenCertificates} disabled={loading} secondary noBorder>
              <WrapperContainer Container={FlexContainers.FlexRowCenter} gap={8}>
                <SVG.CertificateSvg />
                <CertificatesButtonText disabled={loading}>
                  {t(strCode.CERTIFICATES_TITTLE)}
                </CertificatesButtonText>
              </WrapperContainer>
            </SmallButton>
            <DoubleButton title={t(strCode.NEW_BTN)} onClick={onCreateDevice}>
              <IconTextButton onClick={onCreateDevice} icon={<SVG.CreateDeviceIcon />}>
                {strCode.DEVICE_TITLE}
              </IconTextButton>

              <IconTextButton onClick={onCreateBatch} icon={<SVG.BatchCreateDeviceIcon />}>
                {strCode.BATCH_CREATE_BTN}
              </IconTextButton>
            </DoubleButton>
          </WrapperContainer>
        }
        FilterComponent={DeviceFiltersTab}
      />
      {error ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.DEVICES_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : (
        devicesData && (
          <>
            <DeviceTableWrapper>
              <Table
                tableKey="device-table"
                tableInstance={tableInstance}
                searchedText={dusnFilter}
                searchInfo={t(strCode.NO_DEVICES_FOUND, { dusn: dusnFilter })}
                searchCheckInfo={t(strCode.CHECK_DEVICE_NUMBER_INFO)}
                onRowClick={loading && devicesData?.length === 0 ? null : onEdit}
                tooltipLabel={t(strCode.EDIT)}
                isLoading={loading && devicesData?.length === 0}
                hasPagination
              />
            </DeviceTableWrapper>

            <TablePagination
              tableKey="device-table"
              tableSize={rows?.length}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              onPrevPage={previousPage}
              onNextPage={nextPage}
              onFirstPage={() => gotoPage(0)}
              onLastPage={() => gotoPage(pageCount - 1)}
            />
          </>
        )
      )}
    </WrapperContainer>
  );
};

DeviceTable.propTypes = {
  devicesData: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  dusnFilter: PropTypes.string,
  deviceFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSearchValue: PropTypes.func.isRequired,
  onCreateDevice: PropTypes.func.isRequired,
  onCreateBatch: PropTypes.func.isRequired,
  onOpenCertificates: PropTypes.func.isRequired
};

export default DeviceTable;
